// FIXME: Fix import/named export issue for firebase
/* eslint-disable import/named */
import { initializeApp, getApp, getApps } from 'firebase/app'
import {
	initializeAuth,
	browserLocalPersistence,
	browserPopupRedirectResolver,
	browserSessionPersistence,
	getMultiFactorResolver,
	TotpMultiFactorGenerator,
	getAuth
} from 'firebase/auth'
import firebaseUser from './auth-user'
import firebaseAuthLocal from './auth-local'
import firebaseAuthSocial from './auth-social'
import firebaseAuthMail from './auth-mail'
import { createMockFirebaseAuthByToken } from './auth-helper'

function extend (app, mixin) {
	if (!app.mixins) {
		app.mixins = []
	}
	app.mixins.push(mixin)
}

export default ({ app, env, store, $bugsnag, route, redirect }, inject) => {
	const isFirebaseAppInitialized = Boolean(getApps().length)
	let firebaseApp = null

	if (isFirebaseAppInitialized) {
		firebaseApp = getApp()
	} else {
		firebaseApp = initializeApp(env.firebase)
	}

	const firebaseAuth = initializeAuth(firebaseApp, {
		persistence: [browserLocalPersistence, browserSessionPersistence],
		popupRedirectResolver: browserPopupRedirectResolver
	})

	let isInitialized = false

	const $firebase = {
		get initialized () {
			return isInitialized
		},
		set initialized (value) {
			isInitialized = value
		},
		get auth () {
			return process.env.USER_TOKEN ? createMockFirebaseAuthByToken(process.env.USER_TOKEN) : firebaseAuth
		},
		logout: () => firebaseAuthLocal.logout(firebaseAuth),
		login: (email, password, persistence) => firebaseAuthLocal.login(firebaseAuth, email, password, persistence),
		signUpAnonymously: () => firebaseAuthLocal.signUpAnonymously(firebaseAuth),
		getUserSignInMethods: email => firebaseAuthLocal.getUserSignInMethods(firebaseAuth, email),
		loginWithProvider: (provider, loginUserCallback) => firebaseAuthSocial.signInWithProvider(firebaseAuth, provider, loginUserCallback),
		reauthenticate: password => firebaseAuthLocal.reauthenticate(firebaseAuth, password),
		signup: (email, password, persistence) => firebaseAuthLocal.signUp(firebaseAuth, email, password, persistence),
		getUser: (formatted = true) => firebaseUser.getUser(firebaseAuth, formatted),
		getUserIdToken: (currentUser, forceUpdate) => firebaseUser.getUserIdToken(firebaseAuth, currentUser, forceUpdate),
		getUserFormatted: currentUser => firebaseUser.parseUser(currentUser),
		updateUserEmail: email => firebaseUser.updateUserEmail(firebaseAuth, email),
		updateUserProfile: profile => firebaseUser.updateUserProfile(firebaseAuth, profile),
		resetPassword: email => firebaseAuthMail.handleSendPasswordResetEmail(firebaseAuth, email),
		sendEmailVerification: () => firebaseAuthMail.handleSendEmailVerification(firebaseAuth),
		applyEmailRecover: actionCode => firebaseAuthMail.handleApplyRecoverEmailActionCode(firebaseAuth, actionCode),
		applyEmailVerify: actionCode => firebaseAuthMail.handleApplyVerificationEmailActionCode(firebaseAuth, actionCode),
		applyPasswordReset: actionCode => firebaseAuthMail.handleVerifyPasswordResetCode(firebaseAuth, actionCode),
		applyPasswordResetConfirm: (actionCode, newPassword) => firebaseAuthMail.handleConfirmPasswordReset(firebaseAuth, actionCode, newPassword),
		revertSecondFactorAddition: actionCode => firebaseAuthMail.handleUnEnrollMfaByActionCode(firebaseAuth, actionCode),
		getMultiFactorResolver,
		getAuth,
		TotpMultiFactorGenerator,
		subscribeToAuthStateChanges () {
			return $firebase.auth.onAuthStateChanged((currentUser) => {
				if (!this.initialized) {
					this.initialized = true
				}
				if (currentUser) {
					const user = $firebase.getUserFormatted(currentUser)

					store.commit('auth/setUser', user)
				} else {
					store.commit('auth/setUser', null)
				}
			},
			(error) => {
				if (!this.initialized) {
					this.initialized = true
				}

				$bugsnag.notify(error)
			})
		},
		subscribeToIdTokenChanges: () => {
			return $firebase.auth.onIdTokenChanged((currentUser) => {
				if (currentUser) {
					const user = $firebase.getUserFormatted(currentUser)

					store.commit('auth/setUser', user)
				}
			})
		},
		waitOnInitializedChanged () {
			return new Promise((resolve, reject) => {
				const intervalID = setInterval(() => {
					if (!this.initialized) {
						return
					}

					clearInterval(intervalID)
					resolve(true)
				}, 250)
			})
		}
 	}

	window.$firebase = $firebase

	extend(app, {
		data: () => ({
			unsubscribeFromFirebaseAuthStateChanges: null,
			unsubscribeFromFirebaseIdTokenChanges: null
		}),
		created () {
			this.unsubscribeFromFirebaseAuthStateChanges = $firebase.subscribeToIdTokenChanges()
			this.unsubscribeFromFirebaseIdTokenChanges = $firebase.subscribeToAuthStateChanges()
		},
		beforeDestroy () {
			if (this.unsubscribeFromFirebaseAuthStateChanges) {
				this.unsubscribeFromFirebaseAuthStateChanges()
				delete this.unsubscribeFromFirebaseAuthStateChanges
			}

			if (this.unsubscribeFromFirebaseIdTokenChanges) {
				this.unsubscribeFromFirebaseIdTokenChanges()
				delete this.unsubscribeFromFirebaseIdTokenChanges
			}
		}
	})

	inject('firebase', $firebase)
}
